import React, { useState, useEffect } from 'react';
import { artistsAPI } from '../services/api';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface Step1Data {
  firstName: string;
  lastName: string;
  email: string;
}

interface Step2Data {
  pin: string;
  youtubeUrl: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  pin?: string;
  youtubeUrl?: string;
  general?: string;
}

const ArtistSubmission: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [step1Data, setStep1Data] = useState<Step1Data>({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [step2Data, setStep2Data] = useState<Step2Data>({
    pin: '',
    youtubeUrl: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [urlChecking, setUrlChecking] = useState(false);
  const [urlStatus, setUrlStatus] = useState<'safe' | 'unsafe' | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const validateYouTubeUrl = (url: string): boolean => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return youtubeRegex.test(url);
  };

  const handleUrlBlur = async () => {
    if (step2Data.youtubeUrl && validateYouTubeUrl(step2Data.youtubeUrl)) {
      setUrlChecking(true);
      setUrlStatus(null);
      
      try {
        // The backend will check Safe Browsing API on submission
        // Here we just validate format
        setUrlStatus('safe');
      } catch (error) {
        setUrlStatus('unsafe');
      } finally {
        setUrlChecking(false);
      }
    }
  };

  const handleStep1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStep1Data(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleStep2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStep2Data(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!step1Data.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!step1Data.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!step1Data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(step1Data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!step2Data.pin.trim()) {
      newErrors.pin = '6-digit PIN is required';
    } else if (!/^\d{6}$/.test(step2Data.pin)) {
      newErrors.pin = 'PIN must be exactly 6 digits';
    }

    if (!step2Data.youtubeUrl.trim()) {
      newErrors.youtubeUrl = 'YouTube URL is required';
    } else if (!validateYouTubeUrl(step2Data.youtubeUrl)) {
      newErrors.youtubeUrl = 'Please enter a valid YouTube URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep1()) {
      return;
    }

    // Get reCAPTCHA token
    let captchaToken = '';
    try {
      captchaToken = await window.grecaptcha.execute('6Ldj_e8rAAAAABlsxidASfJSM9Y1g9ndyP9q1ylr', { action: 'submit' });
    } catch (error) {
      setErrors({ general: 'reCAPTCHA verification failed. Please refresh the page and try again.' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await artistsAPI.initiateSubmission({
        firstName: step1Data.firstName.trim(),
        lastName: step1Data.lastName.trim(),
        email: step1Data.email.trim(),
        recaptchaToken: captchaToken
      });

      setCurrentStep(2);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Failed to initiate submission. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await artistsAPI.completeSubmission({
        email: step1Data.email.trim(),
        pin: step2Data.pin.trim(),
        youtubeUrl: step2Data.youtubeUrl.trim()
      });

      setSubmitted(true);
      setStep1Data({
        firstName: '',
        lastName: '',
        email: ''
      });
      setStep2Data({
        pin: '',
        youtubeUrl: ''
      });
      setCurrentStep(1);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Failed to complete submission. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setStep2Data({ pin: '', youtubeUrl: '' });
    setErrors({});
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-50 to-primary-50 py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 text-center">
            <div className="mb-6">
              <svg className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Submission Successful!
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6">
              Thank you for submitting your work. You will receive a confirmation email shortly.
            </p>
            <p className="text-sm sm:text-base text-gray-600 mb-8">
              Your submission is currently under review by our team. We will notify you once a decision has been made.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Submit Another Entry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-50 to-primary-50 py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Artist Submission
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Share your musical talent with SoundX724. Submit your YouTube performance for review.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > 1 ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span>1</span>
                )}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900">Your Info</span>
            </div>
            
            <div className={`mx-4 h-1 w-16 sm:w-24 ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-300'}`}></div>
            
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900">Verify & Submit</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
          {currentStep === 1 ? (
            <form onSubmit={handleStep1Submit} className="space-y-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Step 1: Your Information</h2>
                <p className="text-sm text-gray-600">We'll send a verification PIN to your email</p>
              </div>

              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {errors.general}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={step1Data.firstName}
                    onChange={handleStep1Change}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={step1Data.lastName}
                    onChange={handleStep1Change}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={step1Data.email}
                  onChange={handleStep1Change}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  We'll send a 6-digit verification PIN to this email
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending PIN...
                    </span>
                  ) : (
                    'Continue to Next Step'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleStep2Submit} className="space-y-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Step 2: Verify & Submit</h2>
                <p className="text-sm text-gray-600">Check your email for the 6-digit PIN</p>
              </div>

              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {errors.general}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  📧 A 6-digit PIN has been sent to <strong>{step1Data.email}</strong>
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  The PIN will expire in 15 minutes
                </p>
              </div>

              {/* PIN */}
              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                  6-Digit PIN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="pin"
                  name="pin"
                  value={step2Data.pin}
                  onChange={handleStep2Change}
                  maxLength={6}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest font-mono ${
                    errors.pin ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="000000"
                />
                {errors.pin && (
                  <p className="mt-1 text-sm text-red-500">{errors.pin}</p>
                )}
              </div>

              {/* YouTube URL */}
              <div>
                <label htmlFor="youtubeUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  id="youtubeUrl"
                  name="youtubeUrl"
                  value={step2Data.youtubeUrl}
                  onChange={handleStep2Change}
                  onBlur={handleUrlBlur}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.youtubeUrl ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                {errors.youtubeUrl && (
                  <p className="mt-1 text-sm text-red-500">{errors.youtubeUrl}</p>
                )}
                {urlChecking && (
                  <p className="mt-1 text-sm text-gray-500">Checking URL safety...</p>
                )}
                {urlStatus === 'safe' && (
                  <p className="mt-1 text-sm text-green-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    URL is valid
                  </p>
                )}
                {urlStatus === 'unsafe' && (
                  <p className="mt-1 text-sm text-red-500">This URL appears to be unsafe</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Only YouTube URLs are accepted. One submission per year allowed.
                </p>
              </div>

              {/* Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleBackToStep1}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={loading || urlChecking}
                  className={`flex-1 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${
                    loading || urlChecking
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Complete Submission'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 sm:mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Submission Guidelines</h3>
          <ul className="list-disc list-inside text-sm sm:text-base text-blue-800 space-y-2">
            <li>Only YouTube URLs are accepted</li>
            <li>You can submit only one entry per calendar year</li>
            <li>Your submission will be reviewed by our admin team</li>
            <li>You will receive email notifications about the status of your submission</li>
            <li>Ensure your YouTube video is publicly accessible</li>
            <li>The verification PIN expires in 15 minutes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ArtistSubmission;
